import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API, auth, fmtMoney, ORDER_STATUSES, inputCls } from "./crm";

export const Orders = ({ token, reloadKey }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders`, auth(token));
      setOrders(data.orders);
    } catch {
      /* shell handles 401 */
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const update = async (oid, data) => {
    await axios.patch(`${API}/admin/orders/${oid}`, data, auth(token));
    load();
  };

  return (
    <div data-testid="orders-tab" className="bg-white border border-[#E6DEC3] rounded-3xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table data-testid="admin-orders-table" className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="bg-[#F5EFE6] text-left font-mono text-[10px] tracking-[0.18em] uppercase text-[#5C3A21]">
              {["Date", "Buyer", "Product", "Total", "Destination", "Status", "Carrier / Tracking"].map((h) => (
                <th key={h} className="px-4 py-3.5 font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-[#6E5B4F]">
                  {loading ? "Loading…" : "No orders yet. Accept a quotation in a lead's detail panel and convert it to an order."}
                </td>
              </tr>
            )}
            {orders.map((o) => (
              <OrderRow key={o.id} order={o} onUpdate={update} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const OrderRow = ({ order, onUpdate }) => {
  const [carrier, setCarrier] = useState(order.carrier || "");
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const dirty = carrier !== (order.carrier || "") || tracking !== (order.tracking_number || "");
  return (
    <tr data-testid={`order-row-${order.id}`} className="border-t border-[#F5EFE6] align-top">
      <td className="px-4 py-3.5 whitespace-nowrap text-[#6E5B4F]">{new Date(order.created_at).toLocaleDateString()}</td>
      <td className="px-4 py-3.5 whitespace-nowrap">
        <p className="font-semibold text-[#2C1A0C]">{order.lead?.full_name || "—"}</p>
        <p className="text-xs text-[#6E5B4F]">{order.lead?.business_name}</p>
      </td>
      <td className="px-4 py-3.5 whitespace-nowrap">{order.product} · {order.quantity} {order.unit}</td>
      <td className="px-4 py-3.5 whitespace-nowrap font-semibold">{fmtMoney(order.total, order.currency)}</td>
      <td className="px-4 py-3.5 whitespace-nowrap">{order.destination || "—"}</td>
      <td className="px-4 py-3.5 whitespace-nowrap">
        <select
          data-testid={`order-status-${order.id}`}
          value={order.status}
          onChange={(e) => onUpdate(order.id, { status: e.target.value })}
          className="text-xs font-semibold rounded-full border border-[#E6DEC3] px-3 py-1.5 cursor-pointer focus:outline-none bg-white"
        >
          {ORDER_STATUSES.map(([k, l]) => (
            <option key={k} value={k}>{l}</option>
          ))}
        </select>
      </td>
      <td className="px-4 py-3.5 whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <input className={`${inputCls} !py-1.5 !px-2.5 text-xs !w-24`} placeholder="Carrier" value={carrier} onChange={(e) => setCarrier(e.target.value)} />
          <input data-testid={`order-tracking-${order.id}`} className={`${inputCls} !py-1.5 !px-2.5 text-xs !w-32`} placeholder="Tracking #" value={tracking} onChange={(e) => setTracking(e.target.value)} />
          {dirty && (
            <button
              data-testid={`order-save-${order.id}`}
              onClick={() => onUpdate(order.id, { carrier, tracking_number: tracking })}
              className="text-xs font-bold text-[#2E4F3E] underline underline-offset-2"
            >
              Save
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};
